import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import {
  Building2,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Loader2,
  Network,
  RefreshCw,
  Search,
  Table2,
  X,
} from "lucide-react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "../../../Store/Hooks";
import {
  invalidateRelationshipMatrixCache,
  refreshAccountRelationshipMatrix,
} from "../../../Store/Reducers/RelationshipSlice";
import { fetchDepartedRows } from "../../../Store/Reducers/TeamViewsSlice";
import RelationshipEmptyState from "../RelationshipEmptyState";
import { useNavigateToAccountDetails } from "./account-details-setup-message";
import { AccountPickerCard } from "./account-picker-card";
import CoverageMatrixModal from "./coverage-matrix-modal";
import CoverageHistoryModal from "./CoverageHistoryModal";
import { DormantHoleModal } from "./DormantHoleModal";
import MapThisRelationshipModal from "./map-this-relationship-modal";
import MatrixModeLegend from "./matrix-mode-legend";
import { MatrixSetupShell } from "./matrix-setup-overlay";
import { useGrade } from "./RelationshipContexts";
import { useRole } from "./RelationshipContexts";
import {
  recomputeAccountRlAggregate,
  accountShellForOverviewHydrate,
} from "./relationshipDataAdapter";
import RelationshipsMatrixTable from "./RelationshipsMatrixTable";
import RelationshipsNetworkMap from "./RelationshipsNetworkMap";
import {
  findPairForMatrixCell,
  buildPerContactMatrixColumns,
  splitStakeholdersForMatrix,
} from "./relationshipUtils";
import RlAssessmentModal from "./rl-assessment-modal";
import { buildStakeholderLinkFromMatrixCell } from "./stakeholderMappingUtils";

function RelationshipMatrixLoader({ label = "Loading relationship matrix…" }) {
  return (
    <div
      className="matrix-wrap matrix-wrap--loading"
      style={{
        marginTop: 4,
        minHeight: 320,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        background: "#fff",
        border: "1px solid var(--border)",
        borderRadius: 8,
      }}
      aria-busy="true"
      aria-live="polite"
    >
      <Loader2
        className="animate-spin"
        style={{ width: 32, height: 32, color: "var(--brand)" }}
        aria-hidden
      />
      <span
        style={{ fontSize: 13, fontWeight: 500, color: "var(--text-muted)" }}
      >
        {label}
      </span>
    </div>
  );
}

function coverageHighlightPersonName(highlight, stakeholders, contacts) {
  if (!highlight) return "";
  if (highlight.name) return highlight.name;
  const ownerId = highlight.ownerId;
  const fromRoster = ownerId
    ? (stakeholders || []).find(
        s =>
          String(s.id) === String(ownerId) ||
          String(s.sourceId || "") === String(ownerId)
      )?.name
    : "";
  const fromContact = (contacts || []).find(
    c => String(c.id) === String(highlight.contactId)
  )?.name;
  return fromRoster || fromContact || ownerId || highlight.contactId || "";
}

/**
 * @param {object} props
 * @param {Array} props.accounts
 * @param {string|null|undefined} [props.selectedAccountId]
 * @param {(id: string) => void} [props.onSelectAccount]
 * @param {string|null|undefined} [props.executiveId]
 * @param {Function} [props.onPairAssessed]
 * @param {object} [props.vendorContext]
 * @param {Function} [props.onAccountRefreshed]
 * @param {string|null|undefined} [props.preferredViewMode]
 * @param {unknown} [props.preferredViewModeTrigger]
 * @param {string|null|undefined} [props.focusVendorId]
 * @param {string|null|undefined} [props.focusContactId]
 * @param {() => void} [props.onFocusConsumed]
 */
const AccountRelationshipsTab = ({
  accounts,
  selectedAccountId,
  onSelectAccount,
  executiveId,
  onPairAssessed,
  vendorContext,
  onAccountRefreshed,
  preferredViewMode = null,
  preferredViewModeTrigger = null,
  focusVendorId = null,
  focusContactId = null,
  onFocusConsumed,
}) => {
  const dispatch = useAppDispatch();
  const navigateToAccountWorkspace = useNavigateToAccountDetails();
  const { getGradeLabel, gradeMapping } = useGrade();
  const { organization, considerMeddpiccRole, orgSettings } = useRole();
  const considerMeddpiccRoleEnabled = considerMeddpiccRole !== false;
  const orgAggregateSettingsKey = useMemo(() => {
    const settings = orgSettings || organization?.settings || {};
    return `${settings.consider_meddpicc_role}:${settings.unknown_meddpicc_default_weight}`;
  }, [
    orgSettings,
    organization?.settings?.consider_meddpicc_role,
    organization?.settings?.unknown_meddpicc_default_weight,
  ]);
  const [viewMode, setViewMode] = useState("matrix");
  /** Incremented only on matrix → map; map line-draw runs once per generation. */
  const [mapDrawGeneration, setMapDrawGeneration] = useState(0);
  const lastConsumedMapDrawGenRef = useRef(0);
  const handleMapDrawGenerationConsumed = useCallback(gen => {
    lastConsumedMapDrawGenRef.current = gen;
  }, []);
  const [cellMode, setCellMode] = useState("rl");
  const [assessmentTarget, setAssessmentTarget] = useState(null);
  const [mapTarget, setMapTarget] = useState(null);
  const [showCoverageModal, setShowCoverageModal] = useState(false);
  const [showCoverageHistory, setShowCoverageHistory] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const [coverageHighlight, setCoverageHighlight] = useState(null);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("openHistory") === "1") {
      setShowCoverageHistory(true);
    }
    if (params.get("relationshipView") === "map") {
      setViewMode("map");
      setMapDrawGeneration(gen => gen + 1);
    }
    const contactId =
      params.get("highlightContactId") || params.get("contactId") || "";
    const ownerId = params.get("highlightOwnerId") || "";
    const vendorId = params.get("vendorId") || "";
    const contactIds = (params.get("highlightContactIds") || "")
      .split(",")
      .map(part => part.trim())
      .filter(Boolean);
    const highlightName = params.get("highlightName") || "";
    if (!contactId && !ownerId && !vendorId) {
      setCoverageHighlight(null);
      return;
    }
    setCoverageHighlight({
      contactId,
      ownerId: ownerId || vendorId,
      vendorId,
      name: highlightName,
      contactIds: contactId ? [contactId, ...contactIds] : contactIds,
      purpose: params.get("highlightPurpose") || "review",
      what: params.get("highlightWhat") || "",
    });
  }, [location.search]);
  const clearCoverageHighlight = useCallback(() => {
    const params = new URLSearchParams(location.search);
    [
      "highlightContactId",
      "highlightPurpose",
      "highlightOwnerId",
      "highlightContactIds",
      "highlightWhat",
      "highlightName",
      "vendorId",
      "contactId",
    ].forEach(key => params.delete(key));
    setCoverageHighlight(null);
    navigate(
      { pathname: location.pathname, search: params.toString() },
      { replace: true }
    );
  }, [location.pathname, location.search, navigate]);
  const [historyViewRecord, setHistoryViewRecord] = useState(null);
  const [coverageHighlightCell, setCoverageHighlightCell] = useState(null);
  /** Cells where RL assessment was submitted this session — each stays
   * highlighted until the account changes, the page reloads, or the user
   * navigates away. Opening a modal alone does not highlight. */
  const [highlightedCellKeys, setHighlightedCellKeys] = useState(
    () => new Set()
  );
  const buildCellKey = useCallback((stakeholder, contact) => {
    if (!stakeholder || !contact) return null;
    return `${stakeholder.id}:${contact.id}`;
  }, []);
  const markCellHighlighted = useCallback(
    (stakeholder, contact) => {
      const key = buildCellKey(stakeholder, contact);
      if (!key) return;
      setHighlightedCellKeys(prev => {
        if (prev.has(key)) return prev;
        const next = new Set(prev);
        next.add(key);
        return next;
      });
    },
    [buildCellKey]
  );
  /** Compact "Updated" tooltip on the cell just saved — auto-hides after 3s. */
  const [savedToastCellKey, setSavedToastCellKey] = useState(null);
  const savedToastTimeoutRef = useRef(null);
  const showSavedToast = useCallback(
    (stakeholder, contact) => {
      const key = buildCellKey(stakeholder, contact);
      if (!key) return;
      if (savedToastTimeoutRef.current) {
        clearTimeout(savedToastTimeoutRef.current);
      }
      setSavedToastCellKey(key);
      savedToastTimeoutRef.current = setTimeout(() => {
        setSavedToastCellKey(null);
        savedToastTimeoutRef.current = null;
      }, 3000);
    },
    [buildCellKey]
  );
  useEffect(
    () => () => {
      if (savedToastTimeoutRef.current) {
        clearTimeout(savedToastTimeoutRef.current);
      }
    },
    []
  );
  const cardsScrollRef = useRef(null);
  const openCoverageModal = useCallback((highlightFromMap = null) => {
    if (highlightFromMap) {
      setCoverageHighlightCell({
        vendorType: highlightFromMap.vendorType,
        vendorId: highlightFromMap.vendorId,
        customerTeamMemberId: highlightFromMap.customerId,
      });
    } else {
      setCoverageHighlightCell(null);
    }
    setShowCoverageModal(true);
  }, []);

  const closeCoverageModal = useCallback(() => {
    setShowCoverageModal(false);
    setCoverageHighlightCell(null);
  }, []);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [accountSearchQuery, setAccountSearchQuery] = useState("");

  const filteredAccounts = useMemo(() => {
    const query = accountSearchQuery.trim().toLowerCase();
    if (!query) return accounts;
    return accounts.filter(account => {
      const name = String(account.name || "").toLowerCase();
      return (
        name.includes(query) || String(account.id) === String(selectedAccountId)
      );
    });
  }, [accounts, accountSearchQuery, selectedAccountId]);

  const updateCardsScrollState = useCallback(() => {
    const el = cardsScrollRef.current;
    if (!el) return;
    const maxScroll = el.scrollWidth - el.clientWidth;
    setCanScrollLeft(el.scrollLeft > 2);
    setCanScrollRight(maxScroll > 2 && el.scrollLeft < maxScroll - 2);
  }, []);

  const scrollAccountCards = useCallback(direction => {
    const el = cardsScrollRef.current;
    if (!el) return;
    el.scrollBy({ left: direction * 240, behavior: "smooth" });
  }, []);

  useEffect(() => {
    const el = cardsScrollRef.current;
    if (!el) return undefined;
    updateCardsScrollState();
    el.addEventListener("scroll", updateCardsScrollState, { passive: true });
    const ro = new ResizeObserver(updateCardsScrollState);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", updateCardsScrollState);
      ro.disconnect();
    };
  }, [filteredAccounts.length, updateCardsScrollState]);

  useEffect(() => {
    if (!selectedAccountId) return undefined;
    const scrollSelectedIntoView = () => {
      const card = cardsScrollRef.current?.querySelector(
        `[data-acct-card="${selectedAccountId}"]`
      );
      card?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    };
    requestAnimationFrame(() => {
      requestAnimationFrame(scrollSelectedIntoView);
    });
    return undefined;
  }, [selectedAccountId, filteredAccounts.length, accountSearchQuery]);

  const selectViewMode = mode => {
    if (mode === "map" && viewMode === "matrix") {
      setMapDrawGeneration(g => g + 1);
    }
    setViewMode(mode);
  };

  useEffect(() => {
    if (!preferredViewMode || !preferredViewModeTrigger) return;
    if (preferredViewMode === "map" && viewMode === "matrix") {
      setMapDrawGeneration(g => g + 1);
    }
    setViewMode(preferredViewMode);
  }, [preferredViewMode, preferredViewModeTrigger]);

  const selectedAccount = useMemo(
    () =>
      accounts.find(
        account => String(account.id) === String(selectedAccountId)
      ) || accounts[0],
    [accounts, selectedAccountId]
  );

  const [matrixAccount, setMatrixAccount] = useState(null);
  const [matrixRefreshing, setMatrixRefreshing] = useState(false);
  const vendorContextRef = useRef(vendorContext);
  const onAccountRefreshedRef = useRef(onAccountRefreshed);
  const matrixAccountRef = useRef(matrixAccount);
  const refreshRequestIdRef = useRef(0);
  const matrixRefreshInflightRef = useRef(new Map());

  useEffect(() => {
    matrixAccountRef.current = matrixAccount;
  }, [matrixAccount]);

  useEffect(() => {
    setHighlightedCellKeys(new Set());
    setSavedToastCellKey(null);
    if (savedToastTimeoutRef.current) {
      clearTimeout(savedToastTimeoutRef.current);
      savedToastTimeoutRef.current = null;
    }
  }, [selectedAccountId]);

  const refreshMatrixAccount = useCallback(
    async (account, { force = true } = {}) => {
      if (!account?.id) return null;
      const accountId = String(account.id);
      const inflight = matrixRefreshInflightRef.current;
      if (!force && inflight.has(accountId)) {
        return inflight.get(accountId);
      }
      if (force) {
        inflight.delete(accountId);
      }

      const promise = (async () => {
        if (force) {
          dispatch(invalidateRelationshipMatrixCache(accountId));
        }
        const sourceAccount = force
          ? accountShellForOverviewHydrate(account)
          : matrixAccountRef.current?.id === accountId
            ? matrixAccountRef.current
            : account;
        return dispatch(
          refreshAccountRelationshipMatrix({
            account: sourceAccount,
            orgSettings: orgSettings || organization?.settings || {},
            force,
          })
        ).unwrap();
      })().finally(() => {
        inflight.delete(accountId);
      });

      inflight.set(accountId, promise);
      return promise;
    },
    [dispatch, orgSettings, organization?.settings]
  );

  useEffect(() => {
    vendorContextRef.current = vendorContext;
  }, [vendorContext]);

  useEffect(() => {
    onAccountRefreshedRef.current = onAccountRefreshed;
  }, [onAccountRefreshed]);

  useEffect(() => {
    const account =
      accounts.find(item => item.id === selectedAccountId) || accounts[0];
    const accountId = account?.id;
    if (!accountId) {
      setMatrixAccount(null);
      return undefined;
    }
    const requestId = refreshRequestIdRef.current + 1;
    refreshRequestIdRef.current = requestId;
    let cancelled = false;
    setMatrixRefreshing(true);
    setMatrixAccount(null);
    refreshMatrixAccount(account, {
      force: !account.pairs?.length || !account.contacts?.length,
    })
      .then(refreshed => {
        if (
          cancelled ||
          !refreshed ||
          refreshRequestIdRef.current !== requestId
        )
          return;
        setMatrixAccount(refreshed);
        onAccountRefreshedRef.current?.(refreshed);
      })
      .catch(() => {
        if (!cancelled && refreshRequestIdRef.current === requestId) {
          setMatrixAccount(account);
        }
      })
      .finally(() => {
        if (!cancelled && refreshRequestIdRef.current === requestId) {
          setMatrixRefreshing(false);
        }
      });
    return () => {
      cancelled = true;
    };
    // Only refetch when the selected account id changes — not when parent merges refreshed row data.
  }, [selectedAccountId]);

  // Recompute in the effect, not inside a setState updater: React runs
  // updaters while rendering this component, so notifying the parent from
  // one updated RoleBasedLayout mid-render (S-66).
  useEffect(() => {
    const prev = matrixAccountRef.current;
    if (!prev) return;
    const next = recomputeAccountRlAggregate(
      prev,
      orgSettings || organization?.settings || {}
    );
    if (next === prev) return;
    matrixAccountRef.current = next;
    setMatrixAccount(next);
    onAccountRefreshedRef.current?.(next);
  }, [orgAggregateSettingsKey, orgSettings, organization?.settings]);

  const isMatrixLoading =
    matrixRefreshing || matrixAccount?.id !== selectedAccount?.id;
  const hydratedAccount = isMatrixLoading ? null : matrixAccount;
  const contacts = hydratedAccount?.contacts || [];
  const stakeholders = hydratedAccount?.stakeholders || [];
  const pairs = hydratedAccount?.pairs || [];
  const departed = useAppSelector(
    state => state.teamViews.departedByAccount[hydratedAccount?.id || ""] || []
  );

  useEffect(() => {
    if (!isMatrixLoading && hydratedAccount?.id) {
      dispatch(fetchDepartedRows(hydratedAccount.id));
    }
  }, [dispatch, isMatrixLoading, hydratedAccount?.id]);

  const { execTeam, accountTeam } = useMemo(
    () => splitStakeholdersForMatrix(stakeholders),
    [stakeholders]
  );
  const hasVendors = execTeam.length + accountTeam.length > 0;
  const hasCustomers = contacts.length > 0;
  const showMatrixSetupOverlay =
    !isMatrixLoading && (!hasVendors || !hasCustomers);

  const matrixSubtitle = useMemo(() => {
    if (isMatrixLoading) return "Loading relationship data…";
    const vendorPart =
      execTeam.length || accountTeam.length
        ? `${execTeam.length} exec team + ${accountTeam.length} account team`
        : "0 vendors";
    const contactPart = `${contacts.length} contact${contacts.length === 1 ? "" : "s"}`;
    const assessPart = `${hydratedAccount?.assessedPairs ?? 0} of ${hydratedAccount?.mappedPairs ?? 0} pairs assessed`;
    return `${vendorPart} × ${contactPart} · ${assessPart}`;
  }, [
    execTeam.length,
    accountTeam.length,
    contacts.length,
    hydratedAccount,
    isMatrixLoading,
  ]);

  const handleUnmappedCell = useCallback(
    payload => {
      setMapTarget({ ...payload, account: hydratedAccount });
    },
    [hydratedAccount]
  );

  const handleCoverageChanged = useCallback(async () => {
    const account = matrixAccountRef.current;
    if (!account?.id) return;
    setMatrixRefreshing(true);
    try {
      const refreshed = await refreshMatrixAccount(account, { force: true });
      if (refreshed) {
        setMatrixAccount(refreshed);
        onAccountRefreshedRef.current?.(refreshed);
      }
    } finally {
      setMatrixRefreshing(false);
    }
  }, [refreshMatrixAccount]);

  // Blink once for the pair from My Accounts (location.state).
  const blinkStartedRef = useRef(false);
  const onFocusConsumedRef = useRef(onFocusConsumed);
  onFocusConsumedRef.current = onFocusConsumed;

  useEffect(() => {
    if (!focusVendorId || !focusContactId || !hydratedAccount) return undefined;
    if (blinkStartedRef.current) return undefined;
    blinkStartedRef.current = true;
    setViewMode("matrix");

    const keys = [
      `exec-${focusVendorId}:${focusContactId}`,
      `team-${focusVendorId}:${focusContactId}`,
    ];
    let attempts = 0;
    let rafId = 0;

    const tryBlink = () => {
      const el = keys
        .map(k => document.querySelector(`[data-matrix-cell-key="${k}"]`))
        .find(Boolean);
      if (!el) {
        if (++attempts < 60) {
          rafId = requestAnimationFrame(tryBlink);
          return;
        }
        onFocusConsumedRef.current?.();
        return;
      }
      onFocusConsumedRef.current?.();
      el.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center",
      });
      window.setTimeout(() => {
        el.classList.remove("coverage-cell-blink");
        void el.offsetWidth;
        el.classList.add("coverage-cell-blink");
        window.setTimeout(
          () => el.classList.remove("coverage-cell-blink"),
          2400
        );
      }, 500);
    };

    rafId = requestAnimationFrame(tryBlink);
    return () => cancelAnimationFrame(rafId);
  }, [focusVendorId, focusContactId, hydratedAccount]);

  useEffect(() => {
    if (!coverageHighlight || viewMode === "map") return undefined;
    const ids = coverageHighlight.contactIds || [];
    if (!ids.length) return undefined;
    const table = document.getElementById("matrix-table");
    if (!table) return undefined;
    const safe =
      window.CSS && CSS.escape ? CSS.escape(String(ids[0])) : String(ids[0]);
    const th = table.querySelector(`th[data-contact-col="${safe}"]`);
    if (th) th.scrollIntoView({ block: "nearest", inline: "center" });
    return undefined;
  }, [coverageHighlight, viewMode, hydratedAccount, contacts, stakeholders]);

  if (!selectedAccount) {
    return (
      <RelationshipEmptyState
        icon={Network}
        title="No relationship accounts available"
        description="No accounts were found for the selected sponsor(s). Adjust your selection on the Sponsor Selection tab, or verify that sponsors have assigned accounts."
      />
    );
  }

  return (
    <div>
      <section className="acct-picker-panel" aria-label="Account selection">
        <header className="acct-picker-hdr">
          <div className="acct-picker-hdr-title">
            <Building2 className="acct-picker-hdr-icon" aria-hidden />
            <div>
              <div className="acct-picker-hdr-label">Select account</div>
              <div className="acct-picker-hdr-sub">
                {accountSearchQuery
                  ? `${filteredAccounts.length} of ${accounts.length} shown`
                  : `${accounts.length} account${accounts.length === 1 ? "" : "s"}`}
              </div>
            </div>
          </div>
          <div className="acct-picker-hdr-end">
            <div className="acct-picker-search">
              <Search className="acct-picker-search-icon" aria-hidden />
              <input
                type="text"
                role="searchbox"
                className="acct-picker-search-input"
                placeholder="Search accounts…"
                value={accountSearchQuery}
                onChange={event => setAccountSearchQuery(event.target.value)}
                aria-label="Search accounts by name"
              />
              {accountSearchQuery ? (
                <button
                  type="button"
                  className="acct-picker-search-clear"
                  onClick={() => setAccountSearchQuery("")}
                  aria-label="Clear search"
                >
                  <X style={{ width: 14, height: 14 }} aria-hidden />
                </button>
              ) : null}
            </div>
          </div>
        </header>

        <div
          className={`acct-picker-scroll-outer${canScrollLeft ? " can-scroll-left" : ""}${canScrollRight ? " can-scroll-right" : ""}`}
        >
          <button
            type="button"
            className="acct-picker-nav acct-picker-nav--prev"
            aria-label="Scroll accounts left"
            disabled={!canScrollLeft}
            onClick={() => scrollAccountCards(-1)}
          >
            <ChevronLeft style={{ width: 18, height: 18 }} aria-hidden />
          </button>

          <div className="acct-picker-scroll" ref={cardsScrollRef}>
            <div className="acct-picker-track">
              {filteredAccounts.length === 0 ? (
                <div className="acct-picker-empty">
                  <p>
                    No accounts match &ldquo;{accountSearchQuery.trim()}&rdquo;
                  </p>
                  <button
                    type="button"
                    onClick={() => setAccountSearchQuery("")}
                  >
                    Clear search
                  </button>
                </div>
              ) : (
                filteredAccounts.map(account => (
                  <AccountPickerCard
                    key={account.id}
                    account={account}
                    variant="account-relationships"
                    isSelected={
                      String(account.id) === String(selectedAccount.id)
                    }
                    onSelect={onSelectAccount}
                  />
                ))
              )}
            </div>
          </div>

          <button
            type="button"
            className="acct-picker-nav acct-picker-nav--next"
            aria-label="Scroll accounts right"
            disabled={!canScrollRight}
            onClick={() => scrollAccountCards(1)}
          >
            <ChevronRight style={{ width: 18, height: 18 }} aria-hidden />
          </button>
        </div>
      </section>

      <div className="matrix-hdr">
        <div className="matrix-hdr-title">
          <div className="matrix-title">
            {selectedAccount.name}
            {" — "}
            {viewMode === "map" ? "Relationship Map" : "Relationships Matrix"}
          </div>
          <div
            className="text-xs"
            style={{
              color: "var(--text-muted)",
              marginTop: 4,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {matrixSubtitle}
          </div>
        </div>
        <div className="matrix-hdr-actions matrix-actions">
          <div
            className="rel-view-combo"
            role="tablist"
            aria-label="Relationship view"
          >
            <div
              className={`rel-view-combo-matrix${viewMode === "matrix" ? " active" : ""}`}
            >
              <button
                type="button"
                role="tab"
                aria-selected={viewMode === "matrix"}
                className="rel-view-combo-primary"
                onClick={() => selectViewMode("matrix")}
              >
                <Table2
                  style={{ width: 14, height: 14, opacity: 0.85 }}
                  aria-hidden
                />
                Matrix
              </button>
              {viewMode === "matrix" ? (
                <div
                  className="rel-view-combo-subs"
                  role="group"
                  aria-label="Matrix cell data"
                >
                  <span className="rel-view-combo-paren" aria-hidden>
                    (
                  </span>
                  <button
                    type="button"
                    className={`rel-view-combo-sub${cellMode === "rl" ? " active" : ""}`}
                    onClick={() => setCellMode("rl")}
                  >
                    RL
                  </button>
                  <span className="rel-view-combo-div" aria-hidden>
                    |
                  </span>
                  <button
                    type="button"
                    className={`rel-view-combo-sub${cellMode === "goals" ? " active" : ""}`}
                    onClick={() => setCellMode("goals")}
                  >
                    Goals
                  </button>
                  <span className="rel-view-combo-div" aria-hidden>
                    |
                  </span>
                  <button
                    type="button"
                    className={`rel-view-combo-sub${cellMode === "posture" ? " active" : ""}`}
                    onClick={() => setCellMode("posture")}
                  >
                    Posture
                  </button>
                  <span className="rel-view-combo-paren" aria-hidden>
                    )
                  </span>
                </div>
              ) : null}
            </div>
            <div className="rel-view-combo-sep" aria-hidden />
            <button
              type="button"
              role="tab"
              aria-selected={viewMode === "map"}
              className={`rel-view-combo-primary rel-view-combo-map${viewMode === "map" ? " active" : ""}`}
              onClick={() => selectViewMode("map")}
            >
              <Network
                style={{ width: 14, height: 14, opacity: 0.85 }}
                aria-hidden
              />
              Map
            </button>
          </div>
          <button
            type="button"
            className="btn btn-sm btn-sec"
            onClick={() => setShowCoverageHistory(true)}
            disabled={!hydratedAccount?.id}
          >
            History
          </button>
          <button
            type="button"
            className="btn btn-sm btn-sec"
            onClick={() => openCoverageModal()}
          >
            <RefreshCw style={{ width: 14, height: 14 }} /> Update Coverage
          </button>
          <button
            type="button"
            className="btn btn-sm btn-sec"
            onClick={() => navigateToAccountWorkspace(selectedAccount?.id)}
            disabled={!selectedAccount?.id}
          >
            <ExternalLink style={{ width: 14, height: 14 }} /> Account Workspace
          </button>
        </div>
      </div>

      {coverageHighlight ? (
        <div className="matrix-hl-note">
          <span>
            {coverageHighlight.contactId &&
            !coverageHighlight.vendorId &&
            viewMode !== "map" ? (
              <>
                Highlighting{" "}
                <strong>
                  {(contacts || []).find(
                    c => String(c.id) === String(coverageHighlight.contactId)
                  )?.name || coverageHighlight.contactId}
                </strong>{" "}
                —{" "}
                {coverageHighlight.purpose === "add-owner"
                  ? "click a cell in the highlighted column to add an owner."
                  : "their owners and RLs are in the highlighted column."}
              </>
            ) : (
              <>
                Highlighting{" "}
                <strong>
                  {coverageHighlightPersonName(
                    coverageHighlight,
                    stakeholders,
                    contacts
                  )}
                  &apos;s
                </strong>{" "}
                {viewMode === "matrix" ? "row" : "relationships"}
                {viewMode === "matrix" &&
                (coverageHighlight.contactIds || []).length
                  ? " and the column" +
                    ((coverageHighlight.contactIds || []).length === 1
                      ? ""
                      : "s") +
                    " of the " +
                    (coverageHighlight.contactIds || []).length +
                    " " +
                    (coverageHighlight.what || "matching") +
                    " contact" +
                    ((coverageHighlight.contactIds || []).length === 1
                      ? ""
                      : "s")
                  : ""}
              </>
            )}
          </span>
          <button
            type="button"
            className="btn btn-sm btn-sec"
            onClick={clearCoverageHighlight}
          >
            Clear highlight
          </button>
        </div>
      ) : null}

      {isMatrixLoading ? (
        <RelationshipMatrixLoader
          label={
            viewMode === "map"
              ? "Loading relationship map…"
              : "Loading relationship matrix…"
          }
        />
      ) : viewMode === "map" ? (
        <RelationshipsNetworkMap
          accountName={hydratedAccount.name}
          stakeholders={stakeholders}
          contacts={contacts}
          pairs={pairs}
          gradeMapping={gradeMapping}
          getGradeLabel={getGradeLabel}
          considerMeddpiccRole={considerMeddpiccRoleEnabled}
          mapDrawGeneration={mapDrawGeneration}
          initialConsumedMapDrawGen={lastConsumedMapDrawGenRef.current}
          onMapDrawGenerationConsumed={handleMapDrawGenerationConsumed}
          onAssess={setAssessmentTarget}
          onOpenCoverage={() => openCoverageModal()}
          focusVendorId={coverageHighlight?.vendorId || null}
          focusContactId={
            coverageHighlight?.vendorId
              ? null
              : coverageHighlight?.contactId || null
          }
          focusLabel=""
          onClearFocus={clearCoverageHighlight}
        />
      ) : (
        <MatrixSetupShell
          show={showMatrixSetupOverlay}
          accountId={hydratedAccount?.id}
          hasVendors={hasVendors}
          hasCustomers={hasCustomers}
        >
          <MatrixModeLegend cellMode={cellMode} />
          <RelationshipsMatrixTable
            accountId={hydratedAccount.id}
            accountName={hydratedAccount.name}
            orgName={organization?.name}
            contacts={contacts}
            stakeholders={stakeholders}
            pairs={pairs}
            departed={departed}
            cellMode={cellMode}
            gradeMapping={gradeMapping}
            getGradeLabel={getGradeLabel}
            considerMeddpiccRole={considerMeddpiccRoleEnabled}
            onAssess={setAssessmentTarget}
            onUnmappedCell={handleUnmappedCell}
            onCoverageChanged={handleCoverageChanged}
            highlightedCellKeys={highlightedCellKeys}
            savedToastCellKey={savedToastCellKey}
            highlightContactIds={
              viewMode === "matrix"
                ? new Set(coverageHighlight?.contactIds || [])
                : null
            }
            highlightOwnerId={
              viewMode === "matrix" ? coverageHighlight?.ownerId || "" : ""
            }
          />
        </MatrixSetupShell>
      )}

      <RlAssessmentModal
        open={Boolean(assessmentTarget)}
        pair={assessmentTarget?.pair}
        stakeholder={assessmentTarget?.stakeholder}
        contact={assessmentTarget?.contact}
        executiveId={executiveId}
        onClose={() => setAssessmentTarget(null)}
        onSubmitted={async result => {
          if (!hydratedAccount) return;
          const savedStakeholder = assessmentTarget?.stakeholder;
          const savedContact = assessmentTarget?.contact;
          const rejectedInherited = Boolean(result?.rejectedInherited);
          setMatrixRefreshing(true);
          try {
            const refreshed = await refreshMatrixAccount(hydratedAccount, {
              force: true,
            });
            if (refreshed) {
              setMatrixAccount(refreshed);
              onAccountRefreshed?.(refreshed);
            }
            if (!rejectedInherited) {
              onPairAssessed?.();
            }
          } finally {
            setMatrixRefreshing(false);
            // Canceling a pending transfer is not an assessment — do not mark
            // "Assessed this visit" on the (now unmapped) cell.
            if (!rejectedInherited) {
              markCellHighlighted(savedStakeholder, savedContact);
              showSavedToast(savedStakeholder, savedContact);
            }
          }
        }}
      />
      <MapThisRelationshipModal
        open={Boolean(mapTarget)}
        account={mapTarget?.account || hydratedAccount}
        stakeholder={mapTarget?.stakeholder}
        contact={mapTarget?.contact}
        pairs={pairs}
        stakeholders={stakeholders}
        contacts={contacts}
        onClose={() => setMapTarget(null)}
        onOpenCoverageMap={() => {
          const link =
            mapTarget?.stakeholder && mapTarget?.contact
              ? buildStakeholderLinkFromMatrixCell(
                  mapTarget.stakeholder,
                  mapTarget.contact
                )
              : null;
          setMapTarget(null);
          openCoverageModal(link);
        }}
        onMapped={async ({ stakeholder, contact }) => {
          if (!hydratedAccount) return;
          setMatrixRefreshing(true);
          try {
            const refreshed = await refreshMatrixAccount(hydratedAccount, {
              force: true,
            });
            if (!refreshed) return;
            setMatrixAccount(refreshed);
            onAccountRefreshed?.(refreshed);
            const columnDefs = buildPerContactMatrixColumns(
              refreshed.contacts || []
            );
            const col = columnDefs.find(
              column =>
                column.contact?.id === contact.id ||
                (column.contacts || []).some(item => item.id === contact.id)
            );
            if (!col) return;
            const { pair, contact: activeContact } = findPairForMatrixCell(
              refreshed.pairs || [],
              stakeholder.id,
              col
            );
            if (pair) {
              setAssessmentTarget({
                pair,
                stakeholder,
                contact: activeContact || contact,
              });
            }
          } finally {
            setMatrixRefreshing(false);
          }
        }}
      />
      <CoverageHistoryModal
        open={showCoverageHistory}
        accountId={hydratedAccount?.id}
        accountName={hydratedAccount?.name}
        onClose={() => {
          setShowCoverageHistory(false);
          setHistoryViewRecord(null);
        }}
        onViewRecord={record => {
          setHistoryViewRecord(record);
        }}
      />
      <DormantHoleModal
        open={Boolean(historyViewRecord)}
        nested={showCoverageHistory}
        accountId={hydratedAccount?.id}
        onClose={() => setHistoryViewRecord(null)}
        person={{
          user_id: historyViewRecord?.owner_user_id,
          full_name: historyViewRecord?.owner_name,
          role_name: historyViewRecord?.owner_role,
        }}
        hole={
          historyViewRecord
            ? {
                contact_id: historyViewRecord.contact_id,
                contact_name: historyViewRecord.contact_name,
                latest_rl: historyViewRecord.latest_rl,
                has_goal: historyViewRecord.has_goal,
                goal_target_rl: historyViewRecord.goal_target_rl,
                orphan_tier: historyViewRecord.orphan_tier,
              }
            : null
        }
        candidates={[]}
        historyOnly
      />
      <CoverageMatrixModal
        open={showCoverageModal}
        account={hydratedAccount || selectedAccount}
        contacts={contacts}
        pairs={pairs}
        highlightCell={coverageHighlightCell}
        onClose={closeCoverageModal}
        onSaved={async () => {
          const account = matrixAccountRef.current || selectedAccount;
          if (!account) return;
          setMatrixRefreshing(true);
          try {
            const refreshed = await refreshMatrixAccount(account, {
              force: true,
            });
            if (refreshed) {
              setMatrixAccount(refreshed);
              onAccountRefreshed?.(refreshed);
            }
          } finally {
            setMatrixRefreshing(false);
          }
        }}
      />
    </div>
  );
};

export default AccountRelationshipsTab;
